import React, { useState, FC, useRef, RefObject, useEffect, useMemo } from 'react';
import {
    Button, Grid,
    Typography,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';

enum TipoLimiteEnum {
    CANTIDAD_GANANCIA = "CANTIDAD_GANANCIA",
    BALANCE_GANANCIA = "BALANCE_GANANCIA",
    DESPLAZAMIENTO_ACUMULACION = "DESPLAZAMIENTO_ACUMULACION",
    HORAS_ACUMULADAS = "HORAS_ACUMULADAS",
    USO_BALANCES = "USO_BALANCES"
}

enum TipoCheckLimitEnum {
    TEMPORADA = "COMPROBAR LIMITE DURANTE TEMPORADA",
    CONTINUO = "COMPROBAR LIMITE DURANTE CONTINUO",
    PATRON = "COMPROBAR LIMITE EN PATRON"
}

enum TypeShiftLimitEnum {
    MAXIMO = "MAXIMO",
    MAXIMO_PERMITIR_BALANCES_NEGATIVO = "MAXIMO_PERMITIR_BALANCES_NEGATIVO",
    MINIMO = "MINIMO"
}

enum FrequencyUnitTime {
    DIAS = "DIAS",
    SEMANAS = "SEMANAS",
    MESES = "MESES",
    ANOS = "ANOS",
    PERIODO_PAGO = "PERIODO_PAGO"
}

interface InfoProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rowToEdit: any;
    cleanSelected: () => void;
}


function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

const InfoLimits: FC<InfoProps> = ({ open, setOpen, rowToEdit, cleanSelected }) => {

    const { t } = useTranslation()

    const [limit, setLimit] = useState<any>(null);
    //const [datePatternOptions, setDatePatternOptions] = useState<any>([]);

    const [loading, setLoading] = useState(false)


    const typeLimitOptions = useMemo(() => [
        { value: TipoLimiteEnum.CANTIDAD_GANANCIA, label: t("accruals.limits.dialog.inputs.option_type_limit.profit_amount_limit") },
        { value: TipoLimiteEnum.BALANCE_GANANCIA, label: t("accruals.limits.dialog.inputs.option_type_limit.profit_balance_limit") },
        { value: TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION, label: t("accruals.limits.dialog.inputs.option_type_limit.accumulation_displacement_limit") },
        { value: TipoLimiteEnum.HORAS_ACUMULADAS, label: t("accruals.limits.dialog.inputs.option_type_limit.accumulated_hours_limit_minus_the_use_of_accumulation_balances") },
        { value: TipoLimiteEnum.USO_BALANCES, label: t("accruals.limits.dialog.inputs.option_type_limit.limit_on_the_use_of_balances") },
    ], [t]);

    const checkLimitOptions = useMemo(() => [
        { value: TipoCheckLimitEnum.TEMPORADA, label: t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_season") },
        { value: TipoCheckLimitEnum.CONTINUO, label: t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_continuous_operation") },
        { value: TipoCheckLimitEnum.PATRON, label: t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_in_pattern") },
    ], [t]);

    const shiftLimitOptions = useMemo(() => [
        { value: TypeShiftLimitEnum.MAXIMO, label: t("accruals.limits.dialog.inputs.options_type_shift_limit.maximum") },
        { value: TypeShiftLimitEnum.MAXIMO_PERMITIR_BALANCES_NEGATIVO, label: t("accruals.limits.dialog.inputs.options_type_shift_limit.maximum_allowed_negative_balances") },
        { value: TypeShiftLimitEnum.MINIMO, label: t("accruals.limits.dialog.inputs.options_type_shift_limit.minimum") },
    ], [t]);

    const MonthOfYear = useMemo(() => [
        { value: 1, label: t('general.months.january') },
        { value: 2, label: t('general.months.february') },
        { value: 3, label: t('general.months.march') },
        { value: 4, label: t('general.months.april') },
        { value: 5, label: t('general.months.may') },
        { value: 6, label: t('general.months.june') },
        { value: 7, label: t('general.months.july') },
        { value: 8, label: t('general.months.august') },
        { value: 9, label: t('general.months.september') },
        { value: 10, label: t('general.months.october') },
        { value: 11, label: t('general.months.november') },
        { value: 12, label: t('general.months.december') }
    ], [t]);

    const frequencyOptions = useMemo(() => [
        { value: FrequencyUnitTime.DIAS, label: t('accruals.limits.dialog.inputs.options_frequency.days') },
        { value: FrequencyUnitTime.SEMANAS, label: t('accruals.limits.dialog.inputs.options_frequency.weeks') },
        { value: FrequencyUnitTime.MESES, label: t('accruals.limits.dialog.inputs.options_frequency.months') },
        { value: FrequencyUnitTime.ANOS, label: t('accruals.limits.dialog.inputs.options_frequency.years') },
        { value: FrequencyUnitTime.PERIODO_PAGO, label: t('accruals.limits.dialog.inputs.options_frequency.periods_of_pay') }
    ], [t]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const id: number = rowToEdit.id;
                const [datePatterns, limit] = await Promise.allSettled([
                    timekeeperService.date_pattern.getAll(),
                    timekeeperService.limits.getOne(id)
                ])

                if (limit.status === 'fulfilled' && limit.value) {
                    const data = limit ? limit.value : [];
                    setLimit(data);
                }

                if (datePatterns.status === 'fulfilled' && datePatterns.value) {
                    const data = datePatterns ? datePatterns.value : [];
                    console.log('data:', data);
                    //setDatePatternOptions(data);
                }
            } catch (error) {
                console.error('Error fetching limit data or date patterns:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && rowToEdit) {
            fetchData();
        }
    }, [open, rowToEdit]);



    const closeForm = () => {
        setLimit([]);
        //setDatePatternOptions([]);
        setOpen(false);
        setLoading(false);
        cleanSelected();
    }

    const InfoRow = ({ label, value }: { label: string; value: string | number | React.ReactNode }) => (
        <Grid container spacing={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }} direction="row" >
            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <Typography variant="body1">
                    {label}:
                </Typography>

            </Grid>
            <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
                <Typography variant="body1">
                    {value}
                </Typography>
            </Grid>
        </Grid>
    );


    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    return;
                }
                closeForm();
            }}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.limits.dialog.titles.info')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => closeForm()}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                {limit && (
                    <Box>
                        {/* Campo nombre */}
                        <InfoRow
                            label={t('accruals.limits.dialog.inputs.name')}
                            value={limit.name ? limit.name : ''}
                        />
                        {/* Campo Descripción */}
                        <InfoRow
                            label={t('accruals.limits.dialog.inputs.description')}
                            value={limit.description ? limit.description : ''}
                        />
                        {/* Campo Tipo de limite */}
                        <InfoRow
                            label={t('accruals.limits.dialog.inputs.limit_type')}
                            value={`${typeLimitOptions.find(option => option.value === limit.type_limit)?.label} `}
                        />
                        {/* Campo modificado por ETC */}
                        <InfoRow
                            label={t('accruals.limits.dialog.inputs.modify_by_etc')}
                            value={`${limit ? t('general.buttons.yes') : t('general.buttons.no')} `}
                        />
                        {
                            limit.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA && (
                                <InfoRow
                                    label={t('accruals.limits.dialog.inputs.check_limit')}
                                    value={limit ? checkLimitOptions.find(option => option.value === limit.type_check_limit)?.label : ''}
                                />
                            )
                        }
                        {
                            limit.type_check_limit === TipoCheckLimitEnum.TEMPORADA
                            && limit.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA
                            && limit.type_limit !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                            && (
                                <>
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.month')}
                                        value={limit ? MonthOfYear.find(month => month.value === limit.start_month)?.label : ''}
                                    />
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.day')}
                                        value={limit ? limit.start_day : ''}
                                    />
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.month')}
                                        value={limit ? MonthOfYear.find(month => month.value === limit.end_month)?.label : ''}
                                    />
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.day')}
                                        value={limit ? limit.end_day : ''}
                                    />
                                </>
                            )
                        }
                        {
                            limit.type_check_limit === TipoCheckLimitEnum.CONTINUO
                            && limit.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA
                            && limit.type_limit !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                            && (
                                <>
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.frequency')}
                                        value={limit ? limit.frequency_amount : ''}
                                    />
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.frequency_amount')}
                                        value={limit ? frequencyOptions.find(option => option.value === limit.frequency_unit_time)?.label : ''}
                                    />
                                </>
                            )
                        }
                        {
                            limit.type_check_limit === TipoCheckLimitEnum.PATRON
                            && limit.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA
                            && limit.type_limit !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION
                            && (
                                <>
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.date_pattern')}
                                        value={limit ? limit.date_pattern.name : ''}
                                    />
                                </>
                            )
                        }
                        {
                            limit.type_limit === TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION && (
                                <>
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.date_pattern')}
                                        value={limit ? limit.date_pattern.name : ''}
                                    />
                                    <InfoRow
                                        label={t('accruals.limits.dialog.inputs.type_shift_limit')}
                                        value={limit ? shiftLimitOptions.find(option => option.value === limit.type_shift_limit)?.label : ''}
                                    />
                                </>
                            )
                        }
                    </Box>)
                }
                {
                    loading && (
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress color='inherit' size={30} />
                        </Box>
                    )
                }
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={() => closeForm()}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { InfoLimits };